// @flow

import {type Node} from 'react';
import {connect} from 'react-redux';
import sortBy from 'lodash/sortBy';
import groupBy from 'lodash/groupBy';
import uniqBy from 'lodash/uniqBy';
import {createSelector} from 'reselect';

import {renderNullOnError} from 'app/general/renderNullOnError';
import {
  websocketConsumer,
  type websocketConsumerProps,
} from 'app/ws/ws-consumer-hoc';
import type {StoreShapeType} from 'app/reducers/types';
import type {contentType} from 'app/utils/content-type';
import type {BasicUserType} from 'app/user/types';
import type {
  StoredMemberSubscriptionData,
  ConsumableSubscribedUserData,
} from 'app/presence/types';
import {getGroupByDocument} from 'app/presence/utils';

type OwnProps = {|
  contentType: contentType,
  objectId: string | number,
  contextFilter?: (context?: string) => boolean,
  children: ({[string]: Array<ConsumableSubscribedUserData>}) => Node,
|};

type OwnPropsWithWebsocketConsumerProps = {|
  ...websocketConsumerProps,
  ...OwnProps,
|};

type StateProps = {|
  presentMembers: {[string]: Array<ConsumableSubscribedUserData>},
|};

type Props = {|
  dispatch: any,
  ...OwnPropsWithWebsocketConsumerProps,
  ...StateProps,
|};

function BasePresenceConsumer({children, presentMembers}: Props) {
  return children(presentMembers);
}

const getPresentMembersSelector = createSelector<
  // 1st param
  StoreShapeType,
  // 2nd param
  string,
  // return value
  {[string]: Array<ConsumableSubscribedUserData>},
  // T1
  {[string]: BasicUserType},
  // T2
  {[string]: StoredMemberSubscriptionData},
  // T3
  (context?: string) => boolean
>(
  // $FlowFixMe
  (state) => state.userArea.getIn(['usersv2', 'byId']),
  // $FlowFixMe
  (state, channel) =>
    state.presence.getIn(['subscriptions', channel, 'members']),
  (state, channel, contextFilter) => contextFilter,
  (usersMap, members, contextFilter) => {
    /* sort members by joinedAt value.
        Its required to keep the members order consistent.
        Users subscribed for longer times should be the first ones */
    const orderedMembers = sortBy(members || {}, [
      ({groupData}) => groupData.joinedAt,
    ]);

    /* Remove all not required data like timeoutIds */
    const cleanedOrderedMembers = orderedMembers.map(
      ({groupData: {user_id, context, joinedAt, additionalData}}) => ({
        user_id,
        context,
        joinedAt,
        additionalData,
      })
    );

    /* apply the contextFilter func to get only the members which are
        subscribed to the context we are interested to */
    const contextFilteredCleanedOrderedMembers = cleanedOrderedMembers.filter(
      (member) => contextFilter(member.context)
    );

    /* remove duplicated user and contexts
        it happens when same user open the same page/context in more than one tab
        here we'll the keep the older one and discard the most recent one(s) */
    const orderedCleanedMembersWithUniqueUserAndContext = uniqBy(
      contextFilteredCleanedOrderedMembers,
      ({user_id, context, additionalData}) => {
        if (additionalData == null) {
          return `${user_id}:${context}`;
        } else {
          return `${user_id}:${context}:${additionalData.contentType}.${additionalData.objectId}`;
        }
      }
    );
    /* get users data from redux store and store the contexts by user */
    const userAndContext = orderedCleanedMembersWithUniqueUserAndContext
      .map(
        /* get users data from redux store */
        ({user_id, context, joinedAt, additionalData}) => {
          // $FlowFixMe - flow thinks usersMap can be null
          const user = usersMap.get(user_id.toString());

          return {
            context,
            joinedAt,
            additionalData,
            user: user != null ? user.toJS() : null,
          };
        }
      )
      .filter(
        /* filter out the users which we don't have data yet */
        ({user}) => user != null
      );

    /* re-sort the array using the user oldest context joinedAt
        so the sort is consistent even if we users have multiple contexts */
    const sortedUserAndContext = sortBy(userAndContext, [
      ({joinedAt}) => joinedAt,
    ]);

    /* and finally group users by context - returning a dict using the
        context as key and an array of users and joined-at time as items */
    // $FlowFixMe - flow doesn't understand the filter which removes null users
    return groupBy(sortedUserAndContext, ({context}) => context);
  }
);

const mapStateToProps = (
  state: StoreShapeType,
  {
    contentType,
    objectId,
    contextFilter = () => true,
  }: OwnPropsWithWebsocketConsumerProps
): StateProps => ({
  presentMembers: getPresentMembersSelector(
    state,
    getGroupByDocument(contentType, objectId),
    contextFilter
  ),
});

export const PresenceConsumer = renderNullOnError('PresenceConsumer')<OwnProps>(
  websocketConsumer(
    connect<
      Props,
      OwnPropsWithWebsocketConsumerProps,
      StateProps,
      _,
      StoreShapeType,
      _
    >(mapStateToProps)(BasePresenceConsumer)
  )
);
